/**
 * Support Form Page
 * Contact support with issues or questions
 */

import type { LoaderFunctionArgs } from 'react-router';
import { useLoaderData, useActionData, useNavigation, useSearchParams, Form } from 'react-router';
import {
  Page,
  Card,
  BlockStack,
  Text,
  Button,
  Banner,
  InlineStack,
  TextField,
  Select,
  Icon,
  Box,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import { ClockIcon, ChatIcon } from '@shopify/polaris-icons';
import { useState, useEffect } from 'react';
import type { Env } from '../../src/types';
import { useLoading } from '../hooks/shopify';
import { getEnv } from '~/utils/cloudflare-context';
import type { ShopifyShopResponse, ShopRecord } from '../types/api';

// Re-export action from server file for route
export { action } from '../actions/support.server';
import type { SupportActionResult } from '../actions/support.server';

export async function loader({ request, context }: LoaderFunctionArgs) {
  const env = getEnv(context);
  const url = new URL(request.url);
  const shop = url.searchParams.get('shop');

  let shopData: ShopRecord | null = null;
  let shopName = '';
  let contactEmail = '';

  if (shop) {
    // Get shop info from database
    shopData = await env.DB.prepare('SELECT id, shop_domain, access_token FROM shops WHERE shop_domain = ?')
      .bind(shop)
      .first() as ShopRecord | null;

    if (shopData) {
      // Get contact email from shop settings
      const settings = await env.DB.prepare(
        'SELECT email_address FROM shop_settings WHERE shop_id = ?'
      )
        .bind(shopData.id)
        .first();

      contactEmail = (settings?.email_address as string) || '';
      // Settings loaded for shop

      // Fetch shop name from Shopify API
      try {
        const shopInfoResponse = await fetch(
          `https://${shop}/admin/api/2025-01/shop.json`,
          {
            headers: {
              'X-Shopify-Access-Token': shopData.access_token,
            },
          }
        );

        if (shopInfoResponse.ok) {
          const shopInfoData = await shopInfoResponse.json() as ShopifyShopResponse;
          shopName = shopInfoData.shop.name || '';
        }
      } catch (error) {
        // Shop name fetch failed - will use domain fallback
      }

      // Fallback: Extract shop name from domain if API call failed
      if (!shopName) {
        const domainParts = shopData.shop_domain.split('.')[0];
        shopName = domainParts
          .split('-')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }
  }

  return { shop, shopData, shopName, contactEmail };
}

export default function SupportPage() {
  const { shop, shopData, shopName, contactEmail } = useLoaderData<typeof loader>();
  const actionData = useActionData<SupportActionResult>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();

  const isSubmitting = navigation.state === 'submitting';
  const isPageLoading = navigation.state === 'loading';

  // Control App Bridge loading indicator
  useLoading(isPageLoading);

  // Show skeleton page while loading
  if (isPageLoading) {
    return (
      <SkeletonPage
        title="Contact Support"
        backAction
      >
        <BlockStack gap="500">
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={3} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={6} />
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="400">
              <SkeletonDisplayText size="medium" />
              <SkeletonBodyText lines={4} />
            </BlockStack>
          </Card>
        </BlockStack>
      </SkeletonPage>
    );
  }

  const [formState, setFormState] = useState({
    category: 'technical',
    subject: '',
    message: '',
  });

  // Reset form on successful submission
  useEffect(() => {
    if (actionData?.success) {
      setFormState({
        category: 'technical',
        subject: '',
        message: '',
      });
    }
  }, [actionData?.success]);

  const categoryOptions = [
    { label: 'Technical Issue', value: 'technical' },
    { label: 'Billing Question', value: 'billing' },
    { label: 'Feature Request', value: 'feature' },
    { label: 'General Question', value: 'general' },
  ];

  return (
    <div
      className="page-enter"
      style={{
        backgroundColor: '#f4f4f6',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Page background - center-top arc beams */}
      <div className="page-bg-support" aria-hidden="true" />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Page
          title="Contact Support"
          subtitle="Get help from our support team"
          backAction={{
            content: 'Help Center',
            url: `/app/help${queryString ? `?${queryString}` : ''}`,
          }}
        >
          <BlockStack gap="500">
            {/* Email Not Configured Warning */}
            {!contactEmail && (
              <div className="slide-in-up">
                <Banner tone="warning" title="Contact email required">
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd">
                      Please configure your contact email address in Settings before submitting a support ticket.
                    </Text>
                    <InlineStack align="start">
                      <a href={`/app/settings${queryString ? `?${queryString}` : ''}`}>
                        <Button>Go to Settings</Button>
                      </a>
                    </InlineStack>
                  </BlockStack>
                </Banner>
              </div>
            )}

            {/* Success Message */}
            {actionData?.success && (
              <div className="slide-in-up">
                <Banner tone="success" title="Support ticket submitted">
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd">
                      Your ticket number is <strong>{actionData.ticketNumber}</strong>
                    </Text>
                    <Text as="p" variant="bodyMd">
                      We'll respond via email within 24 hours (business days).
                    </Text>
                  </BlockStack>
                </Banner>
              </div>
            )}

            {/* Error Message */}
            {actionData?.error && (
              <div className="slide-in-up">
                <Banner tone="critical" title="Error submitting request">
                  <Text as="p" variant="bodyMd">
                    {actionData.error}
                  </Text>
                </Banner>
              </div>
            )}

            {/* Support Form */}
            <div className="slide-in-up" style={{ animationDelay: '50ms' }}>
              <Card>
                <Form method="post">
                  <input type="hidden" name="shop_domain" value={shop || ''} />
                  <input type="hidden" name="name" value={shopName} />
                  <input type="hidden" name="email" value={contactEmail} />

                  <BlockStack gap="400">
                    <InlineStack gap="300" blockAlign="center">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: '#A5D6A7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <div style={{
                          filter: 'brightness(0) invert(1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Icon source={ChatIcon} tone="base" />
                        </div>
                      </div>
                      <BlockStack gap="100">
                        <Text as="h2" variant="headingMd" fontWeight="semibold">
                          Submit Support Request
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Tell us how we can help you
                        </Text>
                      </BlockStack>
                    </InlineStack>

                    {/* Shop Name - Read-only display */}
                    <Box>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="medium">
                          Shop name
                        </Text>
                        <Box
                          paddingBlock="300"
                          paddingInline="300"
                          borderRadius="200"
                          background="bg-surface-secondary"
                        >
                          <Text as="p" variant="bodyMd" tone="subdued">
                            {shopName || 'Not available'}
                          </Text>
                        </Box>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Contact name from your shop settings
                        </Text>
                      </BlockStack>
                    </Box>

                    {/* Contact Email - Read-only display with link to settings */}
                    <Box>
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="medium">
                          Contact email
                        </Text>
                        <Box
                          paddingBlock="300"
                          paddingInline="300"
                          borderRadius="200"
                          background="bg-surface-secondary"
                        >
                          <Text as="p" variant="bodyMd" tone="subdued">
                            {contactEmail || 'No email configured'}
                          </Text>
                        </Box>
                        <Text as="p" variant="bodySm" tone="subdued">
                          We'll respond to this email address. Update in <a href={`/app/settings${queryString ? `?${queryString}` : ''}`} style={{ color: '#005BD3', textDecoration: 'underline' }}>Settings</a>.
                        </Text>
                      </BlockStack>
                    </Box>

                    <Select
                      label="Category"
                      options={categoryOptions}
                      value={formState.category}
                      onChange={(value) => setFormState({ ...formState, category: value })}
                      name="category"
                      requiredIndicator
                    />

                    <TextField
                      label="Subject"
                      value={formState.subject}
                      onChange={(value) => setFormState({ ...formState, subject: value })}
                      name="subject"
                      autoComplete="off"
                      placeholder="Brief description of your issue"
                      requiredIndicator
                    />

                    <TextField
                      label="Message"
                      value={formState.message}
                      onChange={(value) => setFormState({ ...formState, message: value })}
                      name="message"
                      multiline={6}
                      placeholder="Please provide as much detail as possible..."
                      autoComplete="off"
                      showCharacterCount
                      maxLength={2000}
                      requiredIndicator
                      helpText="Include any error messages, steps to reproduce, or screenshots if relevant"
                    />

                    <InlineStack align="end">
                      <Button 
                        submit 
                        variant="primary" 
                        loading={isSubmitting} 
                        disabled={isSubmitting || !contactEmail}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Form>
              </Card>
            </div>

            {/* Response Time Info */}
            <div className="slide-in-up" style={{ animationDelay: '100ms' }}>
              <Card>
                <BlockStack gap="400">
                  <InlineStack gap="300" blockAlign="center">
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: '#93C5FD',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{
                        filter: 'brightness(0) invert(1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <Icon source={ClockIcon} tone="base" />
                      </div>
                    </div>
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingMd" fontWeight="semibold">
                        What to Expect
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Our support response times and process
                      </Text>
                    </BlockStack>
                  </InlineStack>

                  <BlockStack gap="300">
                    <Box>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Response Time
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Within 24 hours on business days. Critical issues receive priority response within 2 hours.
                      </Text>
                    </Box>

                    <Box>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Email Confirmation
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        You'll receive an automatic confirmation email with your ticket number for tracking.
                      </Text>
                    </Box>

                    <Box>
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        Support Hours
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Monday-Friday, 9 AM - 6 PM EST. Weekend tickets are reviewed on Monday.
                      </Text>
                    </Box>
                  </BlockStack>
                </BlockStack>
              </Card>
            </div>
          </BlockStack>
        </Page>
      </div>
    </div>
  );
}
